import React, { useState, useRef } from "react";
import axios from "axios";
import "../Layout components/Header.css";

const baseURL = process.env.REACT_APP_API_BASE_URL;
const apiKey = process.env.REACT_APP_BROWSER_API_KEY;

async function uploadViaSignedUrl(file, folder, assetType) {
  const res = await axios.post(
    `${baseURL}/upload-urls`,
    {
      files: [{ name: file.name, type: file.type || "image/jpeg" }],
      folder,
      bucket: "original",
      metadata: { asset_type: assetType },
    },
    { headers: { "Content-Type": "application/json", "x-api-key": apiKey } }
  );
  const item = res.data?.uploadUrls?.[0];
  if (!item?.url) throw new Error("No signed URL returned");
  await axios.put(item.url, file, {
    headers: { "Content-Type": file.type || "image/jpeg" },
  });
  const signedUrl = item.url;
  const publicUrl = item.publicUrl || item.url.split("?")[0];
  return { signedUrl, publicUrl };
}

export default function LogoUploadModal({ onClose }) {
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem("currentLogoUrl") || "");
  const [watermarkUrl, setWatermarkUrl] = useState(() => localStorage.getItem("currentWatermarkUrl") || "");
  const [logoSignedUrl, setLogoSignedUrl] = useState("");
  const [watermarkSignedUrl, setWatermarkSignedUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [watermarkUploading, setWatermarkUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewModal, setPreviewModal] = useState({ show: false, url: "", title: "" });

  const logoInputRef = useRef();
  const watermarkInputRef = useRef();

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const { signedUrl, publicUrl } = await uploadViaSignedUrl(file, "logos", "logo");
      localStorage.setItem("currentLogoUrl", publicUrl);
      setLogoUrl(publicUrl);
      setLogoSignedUrl(signedUrl);
    } catch (err) {
      alert("Logo upload failed. Please try again.");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleWatermarkChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setWatermarkUploading(true);
    try {
      const { signedUrl, publicUrl } = await uploadViaSignedUrl(file, "logos", "watermark");
      localStorage.setItem("currentWatermarkUrl", publicUrl);
      setWatermarkUrl(publicUrl);
      setWatermarkSignedUrl(signedUrl);
    } catch (err) {
      alert("Watermark upload failed. Please try again.");
    } finally {
      setWatermarkUploading(false);
    }
  };

  const clearLogo = () => {
    localStorage.removeItem("currentLogoUrl");
    setLogoUrl("");
    if (logoInputRef.current) logoInputRef.current.value = null;
  };

  const clearWatermark = () => {
    localStorage.removeItem("currentWatermarkUrl");
    setWatermarkUrl("");
    if (watermarkInputRef.current) watermarkInputRef.current.value = null;
  };

  const handleSave = async () => {
    if (!logoUrl) {
      alert("Please upload an event logo before saving.");
      return;
    }
    setSaving(true);
    try {
      const eventId = localStorage.getItem("currentEventId");
      const vendorId = localStorage.getItem("vendorId");
      const brandingPayload = {
        event_id: eventId,
        vendor_id: vendorId,
        event_logo: (logoSignedUrl || logoUrl).split("?")[0],
        watermark: (watermarkSignedUrl || watermarkUrl || "").split("?")[0],
      };
      await axios.post(
        `${baseURL}/event/vendor/branding`,
        brandingPayload,
        { headers: { "Content-Type": "application/json", "x-api-key": apiKey } }
      );
      onClose();
      window.location.href = '/eventlist';
    } catch (err) {
      alert("Failed to save branding. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lum-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lum-modal">
        {/* Header */}
        <div className="lum-modal-header">
          <h2>Logo Upload</h2>
          <button className="lum-close-btn" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="assets-section lum-assets">
          {/* Event Logo */}
          <div className="asset-card">
            <div className="asset-header">
              <h3 className="asset-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                Event Logo
              </h3>
              <span className={`asset-badge ${logoUrl ? "uploaded" : "pending"}`}>
                {logoUploading ? "Uploading…" : logoUrl ? "Uploaded" : "Pending"}
              </span>
            </div>
            <input type="file" ref={logoInputRef} style={{ display: "none" }} onChange={handleLogoChange} accept="image/*" disabled={logoUploading} />
            {logoUrl ? (
              <div className="asset-preview">
                <img src={logoUrl} alt="Event Logo" className="preview-thumbnail" onClick={() => setPreviewModal({ show: true, url: logoUrl, title: "Event Logo" })} />
                <div className="asset-actions">
                  <button className="action-btn view-btn" onClick={() => setPreviewModal({ show: true, url: logoUrl, title: "Event Logo" })}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    View
                  </button>
                  <button className="action-btn upload-btn" onClick={() => logoInputRef.current.click()} disabled={logoUploading}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Replace
                  </button>
                  <button className="action-btn remove-btn" onClick={clearLogo}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className={`asset-upload-area${logoUploading ? " asset-upload-area--loading" : ""}`} onClick={() => !logoUploading && logoInputRef.current.click()}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="upload-icon"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <p>{logoUploading ? "Uploading…" : "Click to upload event logo"}</p>
                <span>PNG, JPG or GIF (Max 5MB)</span>
              </div>
            )}
          </div>

          {/* Photographer Watermark */}
          <div className="asset-card">
            <div className="asset-header">
              <h3 className="asset-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <circle cx="17.5" cy="6.5" r="1.5" />
                  <polyline points="21 4 12 4 12 2 2 12 12 22 12 20 21 20" />
                </svg>
                Photographer Watermark
              </h3>
              <span className={`asset-badge ${watermarkUrl ? "uploaded" : "optional"}`}>
                {watermarkUploading ? "Uploading…" : watermarkUrl ? "Uploaded" : "Optional"}
              </span>
            </div>
            <input type="file" ref={watermarkInputRef} style={{ display: "none" }} onChange={handleWatermarkChange} accept="image/*" disabled={watermarkUploading} />
            {watermarkUrl ? (
              <div className="asset-preview">
                <img src={watermarkUrl} alt="Watermark" className="preview-thumbnail" onClick={() => setPreviewModal({ show: true, url: watermarkUrl, title: "Photographer Watermark" })} />
                <div className="asset-actions">
                  <button className="action-btn view-btn" onClick={() => setPreviewModal({ show: true, url: watermarkUrl, title: "Photographer Watermark" })}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    View
                  </button>
                  <button className="action-btn upload-btn" onClick={() => watermarkInputRef.current.click()} disabled={watermarkUploading}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Replace
                  </button>
                  <button className="action-btn remove-btn" onClick={clearWatermark}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className={`asset-upload-area${watermarkUploading ? " asset-upload-area--loading" : ""}`} onClick={() => !watermarkUploading && watermarkInputRef.current.click()}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="upload-icon"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <p>{watermarkUploading ? "Uploading…" : "Click to upload photographer watermark"}</p>
                <span>PNG, JPG or GIF (Max 5MB) — Optional</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="lum-footer">
          <button className="lum-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="lum-save-btn"
            onClick={handleSave}
            disabled={saving || logoUploading || watermarkUploading}
          >
            {saving ? "Saving…" : "Save Branding"}
          </button>
        </div>

        {/* Image preview modal */}
        {previewModal.show && (
          <div className="preview-modal" onClick={() => setPreviewModal({ show: false, url: "", title: "" })}>
            <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="preview-modal-header">
                <h2>{previewModal.title}</h2>
                <button className="preview-close-btn" onClick={() => setPreviewModal({ show: false, url: "", title: "" })}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <img src={previewModal.url} alt={previewModal.title} className="preview-image" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
